import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { ContentProvider } from "./lib/content";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CommandPalette } from "./components/CommandPalette";
import Home from "./pages/Home";
import { AboutPage, ProjectsPage, ServicesPage, BlogPage, ResourcesPage, SpeakingPage, ContactPage, PrivacyPage, TermsPage, NotFoundPage } from "./pages/SubPages";
import AdminPage from "./pages/Admin";
import BlogPost from "./pages/BlogPost";
import RoiToolkit from "./pages/RoiToolkit";
import EnterpriseAiAnalytics from "./pages/EnterpriseAiAnalytics";
import GoogleExecutiveAnalytics from "./pages/GoogleExecutiveAnalytics";
import MediaKit from "./pages/MediaKit";
import "./App.css";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

function Shell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    if (paletteOpen) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [paletteOpen]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <div className="grain min-h-screen">
      <a href="#main-content" data-testid="skip-to-content" className="skip-link">Skip to content</a>
      <ScrollToTop />
      {!isAdmin && <Navbar onOpenPalette={() => setPaletteOpen(true)} />}
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <div id="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/roi-toolkit" element={<RoiToolkit />} />
          <Route path="/projects/enterprise-ai-analytics" element={<EnterpriseAiAnalytics />} />
          <Route path="/projects/google-executive-analytics" element={<GoogleExecutiveAnalytics />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/speaking" element={<SpeakingPage />} />
          <Route path="/media-kit" element={<MediaKit />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          {/* Retired legacy resource URLs — redirect to Knowledge Library so shared links don't 404. */}
          <Route path="/resources/enterprise-ai-readiness-assessment.pdf" element={<Navigate to="/resources" replace />} />
          <Route path="/resources/roi-model-template.pdf" element={<Navigate to="/resources" replace />} />
          <Route path="/resources/responsible-ai-governance-whitepaper.pdf" element={<Navigate to="/resources" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      {!isAdmin && <Footer />}
      <Toaster position="bottom-right" />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <ContentProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </ContentProvider>
    </ThemeProvider>
  );
}
